import { Button, Loader, Stack, Text } from '@mantine/core';
import { ContextModalProps } from '@mantine/modals';
import classnames from 'classnames';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ExpenseList } from 'src/Components/Expenses/ExpenseList';
import useFetchBalanceDetails from 'src/Hooks/Queries/useFetchBalanceDetails';
import useNavigateKeepingSearch from 'src/Hooks/Shared/useNavigateKeepingSearch';
import {
  BalanceDetailItemDto,
  BalanceDetailItemDtoKindEnum,
  BalanceDetailPartyDto,
  ExpensePreviewDto,
} from 'src/Services/API/generated';
import { trackBalanceDetailExpenseOpened } from 'src/Services/Mixpanel/events';
import { formatClosingDate } from 'src/Utils/formatClosingDate';
import { formatEuros } from 'src/Utils/formatEuros';

import styles from './BalanceDetailsModal.module.scss';
import { Props } from './BalanceDetailsModal.types';

const BalanceDetailsModal = ({ context, id, innerProps }: ContextModalProps<Props>) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigateKeepingSearch();

  const { data, isLoading, isError, refetch } = useFetchBalanceDetails({
    assetId: innerProps.assetId,
    userId: innerProps.userId,
    bankAccountId: innerProps.bankAccountId,
    contactId: innerProps.contactId,
    enabled: true,
  });

  const targetName = data?.target?.name ?? data?.target?.firstName ?? '';

  /**
   * The secondary line of each row, keyed by the row id the mapping below hands to the list.
   * A reimbursement says which way the money went; an advance the target has to repay says so,
   * because nothing else on the row explains why it counts in the balance.
   */
  const rowSubtitles = useMemo(() => {
    const subtitles = new Map<string, string>();
    const partyName = (party?: BalanceDetailPartyDto): string =>
      party?.name ?? [party?.firstName, party?.lastName].filter(Boolean).join(' ');

    data?.items?.forEach(item => {
      if (item.kind === BalanceDetailItemDtoKindEnum.Transfer) {
        const rowId = item.transferId ?? item.expenseId;
        if (!rowId) return;
        subtitles.set(
          rowId,
          item.myShareAmount < 0
            ? t('balanceDetails.transferReceived', 'Remboursement reçu de {{from}}', {
                from: partyName(item.from),
              })
            : t('balanceDetails.transferSent', 'Remboursement envoyé à {{to}}', {
                to: partyName(item.to),
              })
        );
        return;
      }

      // An advance of mine the target account still has to repay: the row carries no share of
      // mine, so without this line nothing says why it is on the positive side.
      if (item.expenseId && innerProps.bankAccountId && item.myShareAmount > 0) {
        subtitles.set(
          item.expenseId,
          t('balanceDetails.advancedByYou', 'Avancé par vous · à rembourser par {{account}}', {
            account: targetName,
          })
        );
      }
    });

    return subtitles;
  }, [data?.items, innerProps.bankAccountId, t, targetName]);

  /** Same pill as the operations list — never a second vocabulary for the same fact. */
  const statusOf = (item?: BalanceDetailItemDto) => {
    if (item?.settledSharesCount === undefined || item?.totalSharesCount === undefined) {
      return undefined;
    }
    if (item.totalSharesCount === 0) return undefined;

    return item.settledSharesCount === item.totalSharesCount
      ? { label: t('expenses.status.reimbursed', 'Réglé'), color: 'teal' }
      : {
          label: t('expenses.status.partiallyReimbursed', 'Remboursé {{settled}}/{{total}}', {
            settled: item.settledSharesCount,
            total: item.totalSharesCount,
          }),
          color: 'blue',
        };
  };

  const expenses = useMemo<ExpensePreviewDto[] | undefined>(() => {
    if (!data?.items) return undefined;

    return data.items.map(item => {
      const isTransfer = item.kind === BalanceDetailItemDtoKindEnum.Transfer;

      return {
        // A transfer has no expense page: `handleRowClick` keeps it inert.
        id: isTransfer ? (item.transferId ?? '') : (item.expenseId ?? ''),
        date: item.paidAt,
        title: isTransfer ? t('expenses.transferTitle', 'Remboursement') : item.title,
        // The row must show what this line weighs in the balance, not the total paid:
        // showing the total is what made the modal disagree with the balance it details.
        amount: item.myShareAmount,
        currency: '€',
        isIncome: item.isIncome,
        type: item.type,
        paidBy: item.paidBy,
        paidByBankAccount: item.paidByBankAccount,
        paidByContact: item.paidByContact,
        shares: [],
        isRecurring: false,
        attachmentsCount: 0,
      } as ExpensePreviewDto;
    });
  }, [data?.items, t]);

  const handleRowClick = (expense: ExpensePreviewDto) => {
    const source = data?.items?.find(
      item => item.kind !== BalanceDetailItemDtoKindEnum.Transfer && item.expenseId === expense.id
    );
    if (!source) return;

    trackBalanceDetailExpenseOpened({
      has_settled_shares: (source.settledSharesCount ?? 0) > 0,
    });
    context.closeModal(id);
    navigate(`/asset/${innerProps.assetId}/expenses/${expense.id}`);
  };

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Loader />
      </div>
    );
  }

  if (isError) {
    return (
      <Stack align="center" gap="sm" className={styles.loading}>
        <Text size="sm" c="dimmed" ta="center">
          {t('balanceDetails.error', 'Impossible de charger le détail.')}
        </Text>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          {t('common.retry', 'Réessayer')}
        </Button>
      </Stack>
    );
  }

  if (!expenses || expenses.length === 0) {
    return (
      <Stack align="center" className={styles.loading}>
        <Text size="sm" c="dimmed" ta="center">
          {t('balanceDetails.empty', 'Aucune opération entre vous pour l’instant.')}
        </Text>
      </Stack>
    );
  }

  return (
    <Stack gap="xs">
      {data?.periodStart && (
        <Text size="sm" c="dimmed">
          {t('balanceDetails.periodStart', 'Depuis le {{date}}', {
            date: formatClosingDate(data.periodStart, i18n.language),
          })}
        </Text>
      )}
      <ExpenseList
        expenses={expenses}
        loading={false}
        paidByLabel={t('expenses.list.paidBy', 'Payé par ')}
        onRowClick={handleRowClick}
        hideContactBadge
        getPaidByLine={expense => rowSubtitles.get(expense.id)}
        getStatus={expense =>
          statusOf(
            data?.items?.find(
              item =>
                item.kind !== BalanceDetailItemDtoKindEnum.Transfer && item.expenseId === expense.id
            )
          )
        }
      />
      {data?.totalAmount !== undefined && (
        <div className={styles.totalRow}>
          <span className={styles.totalLabel}>{t('finances.balanceDetail.total', 'Total')}</span>
          <span
            className={classnames(styles.totalAmount, {
              [styles.totalAmountPositive]: data.totalAmount >= 0,
              [styles.totalAmountNegative]: data.totalAmount < 0,
            })}
          >
            {formatEuros(data.totalAmount)}
          </span>
        </div>
      )}
    </Stack>
  );
};

export default BalanceDetailsModal;
